// Stable decode key for a spatial cell value (T34b follow-up).
//
// `useSpatialGeometryPreview` keys its decode effect off this instead of the raw value because two
// engines hand the renderer an OBJECT rather than a hex string: Oracle's `{ __jamSdoGeometry }` and
// MSSQL's `{ __jamMssqlGeometry }` (fork B — the GEOGRAPHY/CLR path). A parent re-render that
// re-creates that object changes its identity without changing the geometry, and keying the effect
// on identity re-ran the decode: the preview dropped back to `loading`, unmounting the map AND the
// Copy WKT / Copy GeoJSON buttons mid-interaction (`handleCopy` also early-returns unless the state
// is `ready`, so a click landing in that window silently copied nothing). PostgreSQL, MySQL and
// SQLite pass hex strings, whose identity is already stable — which is why only Oracle surfaced it.
//
// The key must still CHANGE when the cell's value genuinely changes: `ExpandedValueDialog` stays
// mounted and swaps `value` as the user expands a different cell. So object values are reduced to a
// content hash rather than dropped from the dependency list.
//
// Cost: the hash is O(size) but memoized per object identity in a WeakMap, so the normal path (same
// object across renders) is a single WeakMap lookup. Only a freshly-created object pays the walk —
// and that walk is strictly cheaper than the full re-decode + SRID resolve + map remount it avoids.

/** Per-identity memo, so a stable object is hashed at most once. */
const keyCache = new WeakMap<object, string>();

// FNV-1a over two independent 32-bit lanes (~64 bits of state) — allocation-free and collision-safe
// enough to decide "is this the same cell value", which is the only question asked of it.
type Lanes = { a: number; b: number };

const PRIME_A = 0x01000193;
const PRIME_B = 0x85ebca6b;
const SEED_MIX = 0x9e3779b9;

function mixString(lanes: Lanes, text: string): void {
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    lanes.a = Math.imul(lanes.a ^ code, PRIME_A);
    lanes.b = Math.imul(lanes.b ^ (code + SEED_MIX), PRIME_B);
  }
}

function mixValue(lanes: Lanes, value: unknown, depth: number): void {
  // Guard against a cyclic or pathologically nested value — the markers are flat, so this never
  // trips in practice; it exists so an unexpected shape degrades to a coarse key rather than hangs.
  if (depth > 8) {
    mixString(lanes, ' deep');
    return;
  }
  if (value === null) {
    mixString(lanes, ' null');
    return;
  }
  if (value === undefined) {
    mixString(lanes, ' undef');
    return;
  }
  const type = typeof value;
  if (type === 'number' || type === 'boolean' || type === 'bigint' || type === 'string') {
    mixString(lanes, ` ${type}:${String(value)}`);
    return;
  }
  if (Array.isArray(value)) {
    mixString(lanes, ` [${value.length}`);
    for (const item of value) mixValue(lanes, item, depth + 1);
    return;
  }
  if (ArrayBuffer.isView(value)) {
    const view = value as ArrayBufferView;
    const bytes = new Uint8Array(view.buffer, view.byteOffset, view.byteLength);
    mixString(lanes, ` bin${bytes.length}`);
    for (let i = 0; i < bytes.length; i++) {
      lanes.a = Math.imul(lanes.a ^ bytes[i], PRIME_A);
      lanes.b = Math.imul(lanes.b ^ (bytes[i] + SEED_MIX), PRIME_B);
    }
    return;
  }
  if (type === 'object') {
    // Own enumerable keys, sorted — the markers cross IPC as plain JSON, so key order is incidental.
    const keys = Object.keys(value as Record<string, unknown>).sort();
    mixString(lanes, ` {${keys.length}`);
    for (const key of keys) {
      mixString(lanes, key);
      mixValue(lanes, (value as Record<string, unknown>)[key], depth + 1);
    }
    return;
  }
  mixString(lanes, ` ${type}`);
}

/**
 * A key that is equal whenever two spatial cell values are the same value, and different whenever
 * they are not. Primitives are used directly; objects are reduced to a content hash so that
 * re-creating an equal marker object does not read as a new value.
 */
export function stableSpatialValueKey(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undef';
  const type = typeof value;
  // Strings (hex WKB / WKT on PostgreSQL, MySQL, SQLite) are already stable and cheap to compare.
  if (type === 'string') return `s:${value as string}`;
  if (type === 'number' || type === 'boolean' || type === 'bigint') return `p:${String(value)}`;
  if (type !== 'object' && type !== 'function') return `x:${String(value)}`;

  const obj = value as object;
  const cached = keyCache.get(obj);
  if (cached !== undefined) return cached;
  const lanes: Lanes = { a: 0x811c9dc5, b: 0xcbf29ce4 };
  mixValue(lanes, value, 0);
  const key = `o:${(lanes.a >>> 0).toString(36)}:${(lanes.b >>> 0).toString(36)}`;
  keyCache.set(obj, key);
  return key;
}
